
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';

interface Question {
  id: string;
  type: 'multiple_choice' | 'open_ended' | 'rating' | 'yes_no';
  text: string;
  options?: string[];
  required: boolean;
}

interface Survey {
  id: string;
  title: string;
  description: string;
  questions: Question[];
  is_active: boolean;
  created_at: string;
  share_link_id: string;
  admin_email: string;
  response_count?: number;
}

interface SurveyResponse {
  id: string;
  survey_id: string;
  respondent_email: string;
  respondent_name: string;
  answers: Record<string, string | string[]>;
  respondent_type: 'customer' | 'external';
  created_at: string;
}

const QUESTION_TYPES = [
  { value: 'multiple_choice', label: 'Çoktan Seçmeli', icon: 'ri-list-check' },
  { value: 'open_ended', label: 'Açık Uçlu', icon: 'ri-text' },
  { value: 'rating', label: 'Puanlama (1-5 Yıldız)', icon: 'ri-star-line' },
  { value: 'yes_no', label: 'Evet / Hayır', icon: 'ri-toggle-line' },
];

const generateId = () => Math.random().toString(36).substr(2, 9);

const SurveysPanel = ({ adminEmail }: { adminEmail: string }) => {
  const [view, setView] = useState<'list' | 'create' | 'edit' | 'results'>('list');
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Survey | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formQuestions, setFormQuestions] = useState<Question[]>([]);
  const [formActive, setFormActive] = useState(true);

  const getShareLink = (shareLinkId: string) =>
    `${window.location.origin}/siparis-takibi/survey/${shareLinkId}`;

  const fetchSurveys = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('surveys')
        .select('*')
        .eq('admin_email', adminEmail)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const surveysWithCounts = await Promise.all(
        (data || []).map(async (survey) => {
          const { count } = await supabase
            .from('survey_responses')
            .select('*', { count: 'exact', head: true })
            .eq('survey_id', survey.id);
          return { ...survey, response_count: count || 0 };
        })
      );

      setSurveys(surveysWithCounts);
    } catch (err) {
      console.error('Anketler yüklenirken hata:', err);
    } finally {
      setLoading(false);
    }
  }, [adminEmail]);

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  const resetForm = () => {
    setFormTitle('');
    setFormDescription('');
    setFormQuestions([]);
    setFormActive(true);
  };

  const openCreate = () => {
    resetForm();
    setSelectedSurvey(null);
    setView('create');
  };

  const openEdit = (survey: Survey) => {
    setSelectedSurvey(survey);
    setFormTitle(survey.title);
    setFormDescription(survey.description || '');
    setFormQuestions(survey.questions || []);
    setFormActive(survey.is_active);
    setView('edit');
  };

  const openResults = async (survey: Survey) => {
    setSelectedSurvey(survey);
    setView('results');
    setLoadingResponses(true);
    try {
      const { data, error } = await supabase
        .from('survey_responses')
        .select('*')
        .eq('survey_id', survey.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setResponses(data || []);
    } catch (err) {
      console.error('Yanıtlar yüklenirken hata:', err);
    } finally {
      setLoadingResponses(false);
    }
  };

  const addQuestion = () => {
    const newQ: Question = {
      id: generateId(),
      type: 'open_ended',
      text: '',
      options: [],
      required: false,
    };
    setFormQuestions((prev) => [...prev, newQ]);
  };

  const updateQuestion = (id: string, updates: Partial<Question>) => {
    setFormQuestions((prev) =>
      prev.map((q) => (q.id === id ? { ...q, ...updates } : q))
    );
  };

  const removeQuestion = (id: string) => {
    setFormQuestions((prev) => prev.filter((q) => q.id !== id));
  };

  const moveQuestion = (id: string, direction: 'up' | 'down') => {
    setFormQuestions((prev) => {
      const idx = prev.findIndex((q) => q.id === id);
      if (idx === -1) return prev;
      const newArr = [...prev];
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= newArr.length) return prev;
      [newArr[idx], newArr[swapIdx]] = [newArr[swapIdx], newArr[idx]];
      return newArr;
    });
  };

  const addOption = (questionId: string) => {
    updateQuestion(questionId, {
      options: [
        ...(formQuestions.find((q) => q.id === questionId)?.options || []),
        '',
      ],
    });
  };

  const updateOption = (questionId: string, optionIdx: number, value: string) => {
    const q = formQuestions.find((q) => q.id === questionId);
    if (!q) return;
    const newOptions = [...(q.options || [])];
    newOptions[optionIdx] = value;
    updateQuestion(questionId, { options: newOptions });
  };

  const removeOption = (questionId: string, optionIdx: number) => {
    const q = formQuestions.find((q) => q.id === questionId);
    if (!q) return;
    const newOptions = (q.options || []).filter((_, i) => i !== optionIdx);
    updateQuestion(questionId, { options: newOptions });
  };

  const handleSave = async () => {
    if (!formTitle.trim() || formQuestions.length === 0) return;
    setSaving(true);
    try {
      if (view === 'create') {
        const { error } = await supabase.from('surveys').insert({
          title: formTitle.trim(),
          description: formDescription.trim(),
          questions: formQuestions,
          is_active: formActive,
          admin_email: adminEmail,
        });
        if (error) throw error;
      } else if (view === 'edit' && selectedSurvey) {
        const { error } = await supabase
          .from('surveys')
          .update({
            title: formTitle.trim(),
            description: formDescription.trim(),
            questions: formQuestions,
            is_active: formActive,
          })
          .eq('id', selectedSurvey.id);
        if (error) throw error;
      }
      await fetchSurveys();
      setView('list');
      resetForm();
    } catch (err) {
      console.error('Anket kaydedilirken hata:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (survey: Survey) => {
    try {
      const { error } = await supabase
        .from('surveys')
        .update({ is_active: !survey.is_active })
        .eq('id', survey.id);
      if (error) throw error;
      setSurveys((prev) =>
        prev.map((s) =>
          s.id === survey.id ? { ...s, is_active: !s.is_active } : s
        )
      );
    } catch (err) {
      console.error('Durum güncellenirken hata:', err);
    }
  };

  const handleDelete = async (survey: Survey) => {
    setDeleting(true);
    try {
      const { error } = await supabase.from('surveys').delete().eq('id', survey.id);
      if (error) throw error;
      setSurveys((prev) => prev.filter((s) => s.id !== survey.id));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Anket silinirken hata:', err);
    } finally {
      setDeleting(false);
    }
  };

  const copyShareLink = (survey: Survey) => {
    const link = getShareLink(survey.share_link_id);
    navigator.clipboard
      .writeText(link)
      .then(() => {
        setCopiedId(survey.id);
        setTimeout(() => setCopiedId(null), 2000);
      })
      .catch((err) => console.error('Kopyalama hatası:', err));
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

  // ─── RESULTS VIEW ───────────────────────────────────────────────
  if (view === 'results' && selectedSurvey) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-3 flex-shrink-0">
          <button
            onClick={() => setView('list')}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg"></i>
          </button>
          <div className="w-9 h-9 flex items-center justify-center bg-violet-100 rounded-lg">
            <i className="ri-bar-chart-2-line text-lg text-violet-600"></i>
          </div>
          <div>
            <h1 className="text-gray-900 font-semibold text-base">
              {selectedSurvey.title}
            </h1>
            <p className="text-gray-500 text-xs">{responses.length} yanıt</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 lg:p-6">
          {loadingResponses ? (
            <div className="flex items-center justify-center py-20">
              <i className="ri-loader-4-line text-3xl text-violet-500 animate-spin"></i>
            </div>
          ) : responses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <i className="ri-inbox-line text-5xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-sm">Henüz yanıt gelmedi</p>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl p-4 text-center shadow-sm">
                  <p className="text-2xl font-bold text-violet-600">
                    {responses.length}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">Toplam Yanıt</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4 text-center shadow-sm">
                  <p className="text-2xl font-bold text-emerald-600">
                    {responses.filter((r) => r.respondent_type === 'customer')
                      .length}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">Müşteri</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4 text-center shadow-sm">
                  <p className="text-2xl font-bold text-amber-600">
                    {responses.filter((r) => r.respondent_type === 'external')
                      .length}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">Dışarıdan</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4 text-center shadow-sm">
                  <p className="text-2xl font-bold text-gray-700">
                    {selectedSurvey.questions.length}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">Soru</p>
                </div>
              </div>

              {selectedSurvey.questions.map((q, qi) => {
                const qAnswers = responses
                  .map((r) => r.answers[q.id])
                  .filter(Boolean);
                return (
                  <div
                    key={q.id}
                    className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
                  >
                    <h3 className="text-gray-900 font-medium text-sm mb-4 flex items-start gap-2">
                      <span className="w-6 h-6 flex items-center justify-center bg-violet-100 text-violet-700 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">
                        {qi + 1}
                      </span>
                      {q.text}
                    </h3>

                    {q.type === 'multiple_choice' && (
                      <div className="space-y-2">
                        {(q.options || []).map((opt) => {
                          const count = qAnswers.filter((a) => a === opt).length;
                          const pct =
                            qAnswers.length > 0
                              ? Math.round((count / qAnswers.length) * 100)
                              : 0;
                          return (
                            <div key={opt}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-gray-700 text-sm">{opt}</span>
                                <span className="text-gray-500 text-xs">
                                  {count} yanıt ({pct}%)
                                </span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-2">
                                <div
                                  className="bg-violet-500 h-2 rounded-full transition-all"
                                  style={{ width: `${pct}%` }}
                                ></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {q.type === 'yes_no' && (
                      <div className="flex gap-4">
                        {['Evet', 'Hayır'].map((opt) => {
                          const count = qAnswers.filter((a) => a === opt).length;
                          const pct =
                            qAnswers.length > 0
                              ? Math.round((count / qAnswers.length) * 100)
                              : 0;
                          return (
                            <div
                              key={opt}
                              className={`flex-1 rounded-xl p-4 text-center border ${
                                opt === 'Evet'
                                  ? 'bg-emerald-50 border-emerald-200'
                                  : 'bg-red-50 border-red-200'
                              }`}
                            >
                              <p
                                className={`text-2xl font-bold ${
                                  opt === 'Evet' ? 'text-emerald-600' : 'text-red-600'
                                }`}
                              >
                                {pct}%
                              </p>
                              <p className="text-gray-600 text-sm mt-1">{opt}</p>
                              <p className="text-gray-400 text-xs">{count} yanıt</p>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {q.type === 'rating' && (() => {
                      const nums = qAnswers
                        .map((a) => Number(a))
                        .filter((n) => !isNaN(n));
                      const avg =
                        nums.length > 0
                          ? (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(1)
                          : '-';
                      return (
                        <div>
                          <div className="flex items-center gap-3 mb-3">
                            <span className="text-3xl font-bold text-amber-500">
                              {avg}
                            </span>
                            <div className="flex gap-1">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <i
                                  key={s}
                                  className={`text-xl ${
                                    Number(avg) >= s
                                      ? 'ri-star-fill text-amber-400'
                                      : 'ri-star-line text-gray-300'
                                  }`}
                                ></i>
                              ))}
                            </div>
                            <span className="text-gray-500 text-sm">
                              ({nums.length} yanıt)
                            </span>
                          </div>
                          <div className="space-y-1">
                            {[5, 4, 3, 2, 1].map((s) => {
                              const cnt = nums.filter((n) => n === s).length;
                              const pct =
                                nums.length > 0
                                  ? Math.round((cnt / nums.length) * 100)
                                  : 0;
                              return (
                                <div key={s} className="flex items-center gap-2">
                                  <span className="text-xs text-gray-500 w-4">{s}</span>
                                  <i className="ri-star-fill text-amber-400 text-xs"></i>
                                  <div className="flex-1 bg-gray-100 rounded-full h-2">
                                    <div
                                      className="bg-amber-400 h-2 rounded-full"
                                      style={{ width: `${pct}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-xs text-gray-500 w-8">{cnt}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}

                    {q.type === 'open_ended' && (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {qAnswers.length === 0 ? (
                          <p className="text-gray-400 text-sm italic">Yanıt yok</p>
                        ) : (
                          qAnswers.map((a, i) => (
                            <div
                              key={i}
                              className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
                            >
                              <p className="text-gray-700 text-sm">{String(a)}</p>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                <h3 className="text-gray-900 font-semibold text-sm mb-4 flex items-center gap-2">
                  <i className="ri-list-unordered text-violet-500"></i>
                  Tüm Yanıtlar
                </h3>
                <div className="space-y-3">
                  {responses.map((resp, ri) => (
                    <div key={resp.id} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center">
                            <span className="text-violet-700 text-xs font-bold">{ri + 1}</span>
                          </div>
                          <div>
                            <p className="text-gray-900 text-sm font-medium">
                              {resp.respondent_name || 'İsimsiz'}
                            </p>
                            {resp.respondent_email && (
                              <p className="text-gray-500 text-xs">{resp.respondent_email}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              resp.respondent_type === 'customer'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}
                          >
                            {resp.respondent_type === 'customer' ? 'Müşteri' : 'Dışarıdan'}
                          </span>
                          <span className="text-gray-400 text-xs">{formatDate(resp.created_at)}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {selectedSurvey.questions.map((q) => {
                          const ans = resp.answers[q.id];
                          if (!ans) return null;
                          return (
                            <div key={q.id} className="bg-gray-50 rounded-lg px-3 py-2">
                              <p className="text-gray-500 text-xs mb-1">{q.text}</p>
                              {q.type === 'rating' ? (
                                <div className="flex gap-0.5">
                                  {[1, 2, 3, 4, 5].map((s) => (
                                    <i
                                      key={s}
                                      className={`text-sm ${
                                        Number(ans) >= s
                                          ? 'ri-star-fill text-amber-400'
                                          : 'ri-star-line text-gray-300'
                                      }`}
                                    ></i>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-gray-800 text-sm font-medium">{String(ans)}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── CREATE / EDIT VIEW ─────────────────────────────────────────
  if (view === 'create' || view === 'edit') {
    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-3 flex-shrink-0">
          <button
            onClick={() => {
              setView('list');
              resetForm();
            }}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg"></i>
          </button>
          <div className="w-9 h-9 flex items-center justify-center bg-violet-100 rounded-lg">
            <i
              className={`${
                view === 'create' ? 'ri-add-circle-line' : 'ri-edit-line'
              } text-lg text-violet-600`}
            ></i>
          </div>
          <h1 className="text-gray-900 font-semibold text-base">
            {view === 'create' ? 'Yeni Anket Oluştur' : 'Anketi Düzenle'}
          </h1>
        </div>

        <div className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className="max-w-3xl mx-auto space-y-5">
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <h2 className="text-gray-900 font-semibold text-sm mb-4 flex items-center gap-2">
                <i className="ri-file-text-line text-violet-500"></i>
                Anket Bilgileri
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1.5">
                    Anket Başlığı <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    placeholder="Örn: Müşteri Memnuniyet Anketi"
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none"
                    maxLength={150}
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1.5">
                    Açıklama{' '}
                    <span className="text-gray-400 font-normal">(Opsiyonel)</span>
                  </label>
                  <textarea
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    placeholder="Anket hakkında kısa bir açıklama..."
                    rows={3}
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none resize-none"
                    maxLength={500}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setFormActive(!formActive)}
                    className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                      formActive ? 'bg-violet-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        formActive ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    ></span>
                  </button>
                  <span className="text-gray-700 text-sm">
                    {formActive
                      ? 'Anket Aktif (yanıt alınabilir)'
                      : 'Anket Pasif (yanıt alınamaz)'}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-gray-900 font-semibold text-sm flex items-center gap-2">
                  <i className="ri-question-line text-violet-500"></i>
                  Sorular ({formQuestions.length})
                </h2>
                <button
                  onClick={addQuestion}
                  className="flex items-center gap-2 px-3 py-2 bg-violet-50 hover:bg-violet-100 text-violet-700 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  Soru Ekle
                </button>
              </div>

              {formQuestions.length === 0 ? (
                <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl">
                  <i className="ri-question-line text-4xl text-gray-300 mb-3"></i>
                  <p className="text-gray-500 text-sm">Henüz soru eklenmedi</p>
                  <p className="text-gray-400 text-xs mt-1">
                    Yarıdaki &quot;Soru Ekle&quot; butonuna tıklayın
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {formQuestions.map((q, qi) => (
                    <div
                      key={q.id}
                      className="border border-gray-200 rounded-xl p-4 bg-gray-50/50"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="w-7 h-7 flex items-center justify-center bg-violet-100 text-violet-700 rounded-full text-xs font-bold">
                          {qi + 1}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => moveQuestion(q.id, 'up')}
                            disabled={qi === 0}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer disabled:opacity-30"
                          >
                            <i className="ri-arrow-up-s-line"></i>
                          </button>
                          <button
                            onClick={() => moveQuestion(q.id, 'down')}
                            disabled={qi === formQuestions.length - 1}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer disabled:opacity-30"
                          >
                            <i className="ri-arrow-down-s-line"></i>
                          </button>
                          <button
                            onClick={() => removeQuestion(q.id)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="block text-gray-600 text-xs font-medium mb-1.5">
                          Soru Tipi
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          {QUESTION_TYPES.map((t) => (
                            <button
                              key={t.value}
                              onClick={() =>
                                updateQuestion(q.id, {
                                  type: t.value as Question['type'],
                                  options: t.value === 'multiple_choice' ? [''] : [],
                                })
                              }
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all cursor-pointer ${
                                q.type === t.value
                                  ? 'bg-violet-50 border-violet-300 text-violet-700'
                                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                              }`}
                            >
                              <i className={`${t.icon} text-sm`}></i>
                              {t.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="block text-gray-600 text-xs font-medium mb-1.5">
                          Soru Metni <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={q.text}
                          onChange={(e) => updateQuestion(q.id, { text: e.target.value })}
                          placeholder="Soruyu yazın..."
                          className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none"
                          maxLength={300}
                        />
                      </div>

                      {q.type === 'multiple_choice' && (
                        <div className="mb-3">
                          <label className="block text-gray-600 text-xs font-medium mb-1.5">
                            Seçenekler
                          </label>
                          <div className="space-y-2">
                            {(q.options || []).map((opt, oi) => (
                              <div key={oi} className="flex items-center gap-2">
                                <span className="w-5 h-5 flex items-center justify-center bg-gray-200 rounded-full text-xs text-gray-600 flex-shrink-0">
                                  {String.fromCharCode(65 + oi)}
                                </span>
                                <input
                                  type="text"
                                  value={opt}
                                  onChange={(e) => updateOption(q.id, oi, e.target.value)}
                                  placeholder={`Seçenek ${oi + 1}`}
                                  className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none"
                                  maxLength={200}
                                />
                                <button
                                  onClick={() => removeOption(q.id, oi)}
                                  className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"
                                >
                                  <i className="ri-close-line"></i>
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => addOption(q.id)}
                              className="flex items-center gap-1.5 text-violet-600 hover:text-violet-700 text-xs font-medium cursor-pointer mt-1"
                            >
                              <i className="ri-add-line"></i>
                              Seçenek Ekle
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => updateQuestion(q.id, { required: !q.required })}
                          className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                            q.required ? 'bg-violet-500' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                              q.required ? 'translate-x-5' : 'translate-x-0.5'
                            }`}
                          ></span>
                        </button>
                        <span className="text-gray-600 text-xs">Zorunlu soru</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 pb-6">
              <button
                onClick={() => {
                  setView('list');
                  resetForm();
                }}
                className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors cursor-pointer"
              >
                İptal
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formTitle.trim() || formQuestions.length === 0}
                className="flex-1 px-4 py-3 bg-violet-600 hover:bg-violet-700 text-white font-medium rounded-xl transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>Kaydediliyor...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line"></i>
                    {view === 'create' ? 'Anketi Oluştur' : 'Değişiklikleri Kaydet'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── LIST VIEW ──────────────────────────────────────────────────
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center bg-violet-100 rounded-lg">
            <i className="ri-survey-line text-lg text-violet-600"></i>
          </div>
          <div>
            <h1 className="text-gray-900 font-semibold text-base">Anketler</h1>
            <p className="text-gray-500 text-xs">{surveys.length} anket</p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Yeni Anket
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 lg:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line text-3xl text-violet-500 animate-spin"></i>
          </div>
        ) : surveys.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <i className="ri-survey-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-500 text-sm mb-2">Henüz anket oluşturulmadı</p>
            <p className="text-gray-400 text-xs mb-6">
              İlk anketinizi oluşturmak için butona tıklayın
            </p>
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Yeni Anket Oluştur
            </button>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto space-y-4">
            {surveys.map((survey) => (
              <div key={survey.id} className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-gray-900 font-semibold text-base truncate">
                          {survey.title}
                        </h3>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                            survey.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {survey.is_active ? 'Aktif' : 'Pasif'}
                        </span>
                      </div>
                      {survey.description && (
                        <p className="text-gray-500 text-sm mb-3 line-clamp-2">
                          {survey.description}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-question-line"></i>
                          {survey.questions.length} soru
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line"></i>
                          {survey.response_count} yanıt
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          {formatDate(survey.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Paylaşım Linki */}
                  <div className="mt-4 flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2.5">
                    <i className="ri-link text-violet-400 text-sm flex-shrink-0"></i>
                    <span className="text-violet-700 text-xs truncate flex-1 font-mono">
                      {getShareLink(survey.share_link_id)}
                    </span>
                    <button
                      onClick={() => copyShareLink(survey)}
                      className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-violet-200 hover:bg-violet-100 text-violet-700 text-xs font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
                    >
                      {copiedId === survey.id ? (
                        <>
                          <i className="ri-check-line text-emerald-500"></i>Kopyalandı!
                        </>
                      ) : (
                        <>
                          <i className="ri-file-copy-line"></i>Kopyala
                        </>
                      )}
                    </button>
                  </div>

                  {/* Aksiyonlar */}
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => openResults(survey)}
                      className="flex items-center gap-1.5 px-3 py-2 bg-violet-50 hover:bg-violet-100 text-violet-700 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-bar-chart-2-line"></i>
                      Sonuçlar ({survey.response_count})
                    </button>
                    <button
                      onClick={() => openEdit(survey)}
                      className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-edit-line"></i>
                      Düzenle
                    </button>
                    <button
                      onClick={() => handleToggleActive(survey)}
                      className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                        survey.is_active
                          ? 'bg-amber-50 hover:bg-amber-100 text-amber-700'
                          : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      <i
                        className={survey.is_active ? 'ri-pause-circle-line' : 'ri-play-circle-line'}
                      ></i>
                      {survey.is_active ? 'Pasife Al' : 'Aktife Al'}
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(survey)}
                      className="flex items-center gap-1.5 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ml-auto"
                    >
                      <i className="ri-delete-bin-line"></i>
                      Sil
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-2xl text-red-600"></i>
            </div>
            <h3 className="text-gray-900 font-semibold text-lg text-center mb-2">
              Anketi Silmek İstediğinizden Emin Misiniz?
            </h3>
            <p className="text-gray-600 text-sm text-center mb-6">
              <span className="font-medium text-gray-900">{deleteConfirm.title}</span>{' '}
              anketi ve tüm yanıtları kalıcı olarak silinecek.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {deleting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>Siliniyor...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line"></i>Sil
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveysPanel;
